import { Helmet } from "react-helmet-async";

/**
 * The site we actually run on. The defaults here used to name healthpass.com,
 * a domain this project does not own, so every page that did not pass an
 * explicit url emitted a canonical tag pointing search engines somewhere else
 * — the one meta tag it is worst to get wrong.
 */
const SITE_URL = "https://travelhealthpass.app";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  canonical?: string;
  /** "noindex, nofollow" for pages sent to one reader, such as the partner kit. */
  robots?: string;
}

export function SEO({
  title = "HealthPass - Travel with peace of mind",
  description = "Your emergency health information, available worldwide in seconds. QR code access for first responders. GDPR compliant. Always with you.",
  keywords = "health pass, travel health, emergency medical data, QR code health, GDPR health data, medical records, travel insurance, vaccination records, medication tracker",
  image = "/images/healthpass_og.png",
  url = SITE_URL,
  type = "website",
  canonical,
  robots = "index, follow",
}: SEOProps) {
  const fullTitle = title.includes("HealthPass")
    ? title
    : `${title} | HealthPass`;
  const imageUrl = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  const canonicalUrl = canonical || url;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="HealthPass" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />
      <meta name="twitter:creator" content="@healthpass" />

      {/* Additional Meta Tags */}
      <meta name="robots" content={robots} />
      <meta name="language" content="English" />
      <meta name="author" content="HealthPass" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

      {/* Mobile App Meta */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="HealthPass" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* Theme Color */}
      <meta name="theme-color" content="#2563eb" />
    </Helmet>
  );
}
